/**
 * @file Core methods for manipulating keys
 * QEP1 <=> QTP1 <=> Private => Public => ScriptHash <=> Address
 * Keys are arranged in order of derivation.
 * Arrows determine the direction.
 *
 * QEP1 methods are found within QEP1 standard.
 * All methods take in Big-Endian strings and return Big-Endian strings.
 */
import WIF from 'wif'
import BN from 'bn.js'
import { ec as EC } from 'elliptic'
import base58 from 'bs58'
import * as crypto from 'crypto'
import { hexstring2ab, ab2hexstring, reverseHex, sha256, hash160, hash256 } from '../utils'
import { ADDR_VERSION } from '../consts'
import secureRandom from 'secure-random'

const curve = new EC('p256')

/**
 * @param {string} publickey - unencoded public key
 * @return {string} encoded public key
 */
export const getPublicKeyEncoded = (publicKey) => {
  let publicKeyArray = hexstring2ab(publicKey)
  if (publicKeyArray[64] % 2 === 1) {
    return '03' + ab2hexstring(publicKeyArray.slice(1, 33))
  } else {
    return '02' + ab2hexstring(publicKeyArray.slice(1, 33))
  }
}

/**
 * @param {string} publicKey - Encoded public key
 * @return {string} decoded public key
 */
export const getPublicKeyUnencoded = (publicKey) => {
  let keyPair = curve.keyFromPublic(publicKey, 'hex')
  return keyPair.getPublic().encode('hex')
}

/**
 * @param {string} wif
 * @return {string}
 */
export const getPrivateKeyFromWIF = (wif) => {
  return ab2hexstring(WIF.decode(wif, 128).privateKey)
}

/**
 * @param {string} privateKey
 * @return {string}
 */
export const getWIFFromPrivateKey = (privateKey) => {
  return WIF.encode(128, Buffer.from(privateKey, 'hex'), true)
}

/**
 * Calculates the public key from a given private key.
 * @param {string} privateKey
 * @param {boolean} encode - Returns the encoded form if true.
 * @return {string}
 */
export const getPublicKeyFromPrivateKey = (privateKey, encode = true) => {
  const curve = new EC('p256')
  const keypair = curve.keyFromPrivate(privateKey, 'hex')
  const unencodedPubKey = keypair.getPublic().encode('hex')
  if (encode) {
    let tail = parseInt(unencodedPubKey.substr(64 * 2, 2), 16)
    if (tail % 2 === 1) {
      return '03' + unencodedPubKey.substr(2, 64)
    } else {
      return '02' + unencodedPubKey.substr(2, 64)
    }
  } else return unencodedPubKey
}

/**
 * Get encoded ecc point string
 * @param {string} pubKey
 * @return {string}
 */
export const getEncodedEcc = (pubkey) => {
  const unencodedPubKey = pubkey
  let tail = parseInt(unencodedPubKey.substr(64 * 2, 2), 16)
  if (tail % 2 === 1) {
    return '03' + unencodedPubKey.substr(2, 64)
  } else {
    return '02' + unencodedPubKey.substr(2, 64)
  }
}

/**
 * VerificationScript serves a very niche purpose.
 * It is attached as part of the signature when signing a transaction.
 * Thus, the name 'scriptHash' instead of 'keyHash' is because we are hashing the verificationScript and not the PublicKey.
 * @param {string} publicKey
 * @return {string}
 */
export const getVerificationScriptFromPublicKey = (publicKey) => {
  return '21' + publicKey + 'ac'
  // return publicKey
}

export const getMultiSignVerificationScriptFromPublicKey = (publicKey) => {
  return '21' + publicKey
  // return publicKey
}

/**
 * @param {string} publicKey
 * @return {string}
 */
export const getScriptHashFromPublicKey = (publicKey) => {
  // if unencoded
  if (publicKey.substring(0, 2) === '04') {
    publicKey = getPublicKeyEncoded(publicKey)
  }
  const verificationScript = getVerificationScriptFromPublicKey(publicKey)
  return reverseHex(hash160(verificationScript))
}

/**
 * @param {string} scriptHash
 * @return {string}
 */
export const getAddressFromScriptHash = (scriptHash) => {
  scriptHash = reverseHex(scriptHash)
  const shaChecksum = hash256(ADDR_VERSION + scriptHash).substr(0, 8)
  return base58.encode(Buffer.from(ADDR_VERSION + scriptHash + shaChecksum, 'hex'))
}

/**
 * @param {string} address
 * @return {string}
 */
export const getScriptHashFromAddress = (address) => {
  let hash = ab2hexstring(base58.decode(address))
  return reverseHex(hash.substr(2, 40))
}

/**
 * Generates a signature of the transaction based on given private key.
 * @param {string} hex - Serialized unsigned transaction. or hexstring.
 * @param {string} privateKey - Private Key.
 * @return {string} Signature. Does not include tx.
 */
export const generateSignature = (hex, privateKey) => {
  const msgHash = sha256(hex)
  const msgHashHex = Buffer.from(msgHash, 'hex')

  let elliptic = new EC('p256')
  const sig = elliptic.sign(msgHashHex, privateKey, null)
  const signature = Buffer.concat([
    sig.r.toArrayLike(Buffer, 'be', 32),
    sig.s.toArrayLike(Buffer, 'be', 32)
  ])

  return signature.toString('hex')
}

/**
 * Verifies a hexstring is signed by public key.
 * @param {string} hex - Original message in HEX.
 * @param {string} signature - HEX signature.
 * @param {string} publicKey - public key.
 * @return {bool} Validity of signature.
 */
export const verifySignature = (hex, signature, publicKey) => {
  const sig = getSignatureFromHex(signature)

  const messageHash = sha256(hex)
  return curve.verify(messageHash, sig, publicKey, 'hex')
}

/**
 * Converts signatureHex to a signature object with r & s.
 * @param {string} signatureHex
 */
const getSignatureFromHex = signatureHex => {
  const signatureBuffer = Buffer.from(signatureHex, 'hex')
  const r = new BN(signatureBuffer.slice(0, 32).toString('hex'), 16, 'be')
  const s = new BN(signatureBuffer.slice(32).toString('hex'), 16, 'be')
  return {
    r: r,
    s: s
  }
}

/**
 * Generates a random private key
 * @returns {string}
 */
export const generatePrivateKey = () => {
  return ab2hexstring(secureRandom(32))
}

/**
 * Generates a arrayBuffer filled with random bits.
 * @param {number} length - Length of buffer.
 * @returns {ArrayBuffer}
 */
export const generateRandomArray = (length) => {
  return secureRandom(length)
}

/** Encrypt the plain data
 * @param {string} plaintext - plain text to encrypt.
 * @param {string} prikey - my one-time prikey.
 * @param {string} pubkey - server's one-time pubkey.
 * @returns {string} - returns encrypted data.
 */
export const processEncrypt = (plaintext, prikey, pubkey) => {
  const encKey = new EC('p256').genKeyPair().getPublic()
  var serverPubkey = new EC('p256').keyFromPublic(pubkey, 'hex').getPublic()
  var clientPrikey = new EC('p256').keyFromPrivate(prikey, 'hex').getPrivate()
  var seedKey = serverPubkey.mul(clientPrikey).add(encKey).encode('hex')
  seedKey = getEncodedEcc(seedKey)

  var enckeystr = sha256(getEncodedEcc(encKey.encode('hex')))

  var cipherText = AESEncrypt(plaintext, enckeystr.slice(0, 32))
  var ret = {
    server_pubkey: pubkey,
    client_pubkey: getPublicKeyFromPrivateKey(prikey),
    seed_key: seedKey,
    encrypted: cipherText
  }

  return ret
}

/** Decrypt the cipher data
 * @param {string} plaintext - cipher text to decrypt.
 * @param {string} prikey - my one-time prikey.
 * @returns {any} - returns decrypted data.
 */
export const processDecrypt = (cipherText, prikey) => {
  var cipherObject = cipherText

  var seedKey = new EC('p256').keyFromPublic(cipherObject.seed_key, 'hex').getPublic()
  var serverPubkey = cipherObject.server_pubkey
  var clientPrikey = new EC('p256').keyFromPrivate(prikey, 'hex').getPrivate()
  var encKey = new EC('p256').keyFromPublic(serverPubkey, 'hex').getPublic().mul(clientPrikey)
  encKey = seedKey.add(encKey.neg())

  var enckeystr = sha256(getEncodedEcc(encKey.encode('hex')))

  var plainText = AESDecrypt(cipherText.encrypted, enckeystr.slice(0, 32))

  return JSON.parse(plainText)
}

/** AES256 - CBC Encrypt
 * @param {string} plainText - plain text to encrypt
 * @param {string} keyString - key string for the encryption, length must be 32
 * @returns {string} - returns encrypted string
 */
export const AESEncrypt = (plainText, keyString) => {
  const IV_SIZE = 16
  const iv = crypto.randomBytes(IV_SIZE)
  const cipher = crypto.createCipheriv('aes-256-cbc', keyString, iv)
  let cipherText = cipher.update(Buffer.from(plainText, 'utf8'))
  cipherText = Buffer.concat([cipherText, cipher.final()])
  const combinedData = Buffer.concat([iv, cipherText])
  const combinedString = combinedData.toString('base64')
  return combinedString
}

/** AES256 - CBC Decrypt
 * @param {string} combinedString - string to decrypt
 * @param {string} keyString - key string for the decryption, length must be 32
 * @returns {string} - returns decrypted plain text
 */
export const AESDecrypt = (combinedString, keyString) => {
  const IV_SIZE = 16
  const combinedData = Buffer.from(combinedString, 'base64')
  const iv = Buffer.alloc(IV_SIZE)
  const cipherText = Buffer.alloc(combinedData.length - iv.length)
  combinedData.copy(iv, 0, 0, iv.length)
  combinedData.copy(cipherText, 0, iv.length)
  const decipher = crypto.createDecipheriv('aes-256-cbc', keyString, iv)
  let plainText = decipher.update(cipherText, 'utf8')
  plainText += decipher.final('utf8')
  return plainText
}
